/**
 * site_picker_controller.js — site/address picker for quote/enquiry forms (Stimulus).
 *
 * Wraps the [data-site-picker] block: a <select name="site_id"> whose options
 * reload per selected client (async), plus an inline "new address" editor.
 *
 * Why its own controller, not data-controller="select": the site <select>'s
 * options are rebuilt on every client change (destroyChoices -> repopulate ->
 * initChoices), so the generic select controller's build-once lifecycle does not
 * fit. This controller owns the same Choices instance but adds the lifecycle the
 * old standalone site_picker.js lacked: a connect-scoped turbo:before-cache that
 * destroys Choices (and clears the DOM-dataset init guards) BEFORE Turbo
 * snapshots the page. Without it the cached <select> kept a dead Choices wrapper
 * and a stale init guard, so the native value silently desynced from the visible
 * chip on return navigation — the same Choices/Turbo-cache desync class fixed by
 * select_controller.js.
 *
 * Loaded by PageLoader (base.html) with site-api.js and Choices.js ordered
 * before it, so window.apiClient, window.siteApi, and Choices are defined
 * when connect() runs.
 *
 * Markup contract:
 *   <div data-controller="site-picker" data-site-picker data-client-select="#client_id">
 *     <div data-site-picker-chooser>      (hidden while the new-address panel is open)
 *       <select name="site_id" data-site-picker-select></select>
 *     </div>
 *     <div data-site-picker-new-address>
 *       <input data-site-picker-new-address-input data-address-autocomplete>
 *       <button data-site-picker-new-address-save></button>
 *     </div>
 *   </div>
 */
import { Controller } from "@hotwired/stimulus";

    var NEW_ADDRESS_VALUE = '__new__';

    var ADDRESS_FIELDS = ['street', 'city', 'state', 'postcode', 'latitude', 'longitude'];

    function destroyChoices(select) {
        if (select && select.choicesInstance) {
            select.choicesInstance.destroy();
            select.choicesInstance = null;
        }
    }

    function initChoices(select, retries) {
        if (!select || select.choicesInstance) return;
        if (typeof retries === 'undefined') retries = 5;
        if (typeof Choices === 'undefined') {
            if (retries > 0) {
                setTimeout(function () { initChoices(select, retries - 1); }, 100);
            }
            return;
        }
        select.choicesInstance = new Choices(select, {
            searchEnabled: true,
            placeholder: false,
            itemSelectText: '',
            shouldSort: false,
            searchPlaceholderValue: 'Search addresses...'
        });
    }

    function setPickerValue(select, value) {
        var str = String(value == null ? '' : value);
        if (select.choicesInstance) {
            select.choicesInstance.setChoiceByValue(str);
        } else {
            select.value = str;
        }
    }

    function fieldEl(picker, key) {
        return picker.querySelector('[data-site-picker-new-address-' + key + ']');
    }

    function readField(picker, key) {
        var el = fieldEl(picker, key);
        return el ? el.value : '';
    }

    function writeField(picker, key, value) {
        var el = fieldEl(picker, key);
        if (el) el.value = value || '';
    }

    function labelForAddress(address) {
        var parts = [];
        if (address.label) parts.push(address.label);
        if (address.full_address) parts.push(address.full_address.replace(/\n/g, ', '));
        if (!parts.length && address.address_input) parts.push(address.address_input);
        if (!parts.length) parts.push('Address #' + address.id);
        if (address.is_primary) parts.push('Primary');
        return parts.join(' - ');
    }

    // The sites and the one to start at. The server owns which site that is
    // (SiteService.default_site_id_for); a business with several has none.
    async function fetchAddresses(clientId) {
        var data = await window.siteApi.list(clientId);
        return {
            sites: (data && data.sites) ? data.sites : [],
            defaultId: data ? data.default_site_id : null
        };
    }

    function setLoading(select, isLoading) {
        if (isLoading) {
            destroyChoices(select);
            select.innerHTML = '<option value="">Loading addresses...</option>';
        }
        select.disabled = isLoading;
    }

    function appendPlaceholder(select, text) {
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = text;
        placeholder.selected = true;
        select.appendChild(placeholder);
    }

    function renderAddresses(select, addresses, selectedValue, allowCreate, defaultId) {
        var currentValue = typeof selectedValue !== 'undefined' ? String(selectedValue || '') : String(select.value || '');
        var wanted = currentValue || (defaultId ? String(defaultId) : '');
        destroyChoices(select);
        select.innerHTML = '';

        if (addresses.length === 0) {
            appendPlaceholder(select, allowCreate ? 'No saved addresses' : 'Select a client first');
        } else if (!addresses.some(function (address) { return String(address.id) === wanted; })) {
            // No default to start at: the person picks the site rather than the
            // form quietly putting the work at the first one.
            appendPlaceholder(select, 'Choose a site');
        }

        var effectiveValue = '';
        addresses.forEach(function (address) {
            var option = document.createElement('option');
            option.value = address.id;
            option.textContent = labelForAddress(address);
            if (String(address.id) === wanted) {
                option.selected = true;
                effectiveValue = wanted;
            }
            select.appendChild(option);
        });

        if (allowCreate) {
            var createOption = document.createElement('option');
            createOption.value = NEW_ADDRESS_VALUE;
            createOption.textContent = '+ Add new site address';
            select.appendChild(createOption);
        }

        select.disabled = false;
        initChoices(select);
        // Choices.js can desync from the pre-selected <option> when re-wrapping;
        // force the visible chip and underlying value to match what we chose.
        if (effectiveValue) setPickerValue(select, effectiveValue);
    }

    function getPickerForm(picker) {
        return picker.closest('form');
    }

    function clearNewAddressErrors(picker) {
        var error = picker.querySelector('[data-site-picker-new-address-error]');
        if (error) {
            error.textContent = '';
            error.classList.add('d-none');
        }
    }

    function setNewAddressError(picker, message) {
        var error = picker.querySelector('[data-site-picker-new-address-error]');
        if (!error) return;
        error.textContent = message;
        error.classList.remove('d-none');
    }

    function clearNewAddressInputs(picker) {
        ['input', 'label'].concat(ADDRESS_FIELDS).forEach(function (key) {
            writeField(picker, key, '');
        });
        clearNewAddressErrors(picker);
    }

    // The new-address panel takes the dropdown's place rather than sitting
    // under it: left showing, the dropdown reads "+ Add new site address" as
    // if that were the chosen site.
    function setChooserVisible(picker, visible) {
        var chooser = picker.querySelector('[data-site-picker-chooser]');
        if (chooser) chooser.classList.toggle('d-none', !visible);
    }

    function hideNewAddressForm(picker) {
        var form = picker.querySelector('[data-site-picker-new-address]');
        if (form) form.classList.add('d-none');
        setChooserVisible(picker, true);
        picker.dataset.sitePickerCreatingAddress = 'false';
        clearNewAddressErrors(picker);
    }

    // shouldFocus is false when the panel opens by itself for a client with
    // no saved sites, so choosing the client does not raise the keyboard.
    function showNewAddressForm(picker, shouldFocus) {
        var form = picker.querySelector('[data-site-picker-new-address]');
        if (!form) return;
        form.classList.remove('d-none');
        setChooserVisible(picker, false);
        // With no saved site there is nothing to go back to.
        var backToSaved = picker.querySelector('[data-site-picker-new-address-cancel]');
        if (backToSaved) backToSaved.classList.toggle('d-none', picker.dataset.sitePickerHasSites !== 'true');
        picker.dataset.sitePickerCreatingAddress = 'true';
        clearNewAddressErrors(picker);

        var input = picker.querySelector('[data-site-picker-new-address-input]');
        if (input && !input.autocompleteInstance && typeof window.AddressAutocomplete !== 'undefined') {
            input.autocompleteInstance = new window.AddressAutocomplete(input, {
                country: picker.closest('[data-region]') ? picker.closest('[data-region]').dataset.region : 'AU',
                onSelect: function (suggestion) {
                    ADDRESS_FIELDS.forEach(function (key) {
                        writeField(picker, key, suggestion[key]);
                    });
                }
            });
        }

        if (input && shouldFocus) {
            input.focus();
        }
    }

    function buildNewAddressPayload(picker) {
        var payload = {
            address_input: readField(picker, 'input').trim(),
            label: readField(picker, 'label').trim()
        };
        ADDRESS_FIELDS.forEach(function (key) {
            payload[key] = readField(picker, key);
        });
        return payload;
    }

    function payloadHasCoordinates(payload) {
        return payload.latitude || payload.longitude;
    }

    async function createAddress(picker, clientId) {
        var payload = buildNewAddressPayload(picker);
        if (!payload.address_input) {
            setNewAddressError(picker, 'Enter an address before saving.');
            return null;
        }

        var requestBody = {
            address_input: payload.address_input
        };

        if (payload.label) requestBody.label = payload.label;
        if (payload.street) requestBody.street = payload.street;
        if (payload.city) requestBody.city = payload.city;
        if (payload.state) requestBody.state = payload.state;
        if (payload.postcode) requestBody.postcode = payload.postcode;
        if (payloadHasCoordinates(payload)) {
            requestBody.latitude = payload.latitude ? parseFloat(payload.latitude) : null;
            requestBody.longitude = payload.longitude ? parseFloat(payload.longitude) : null;
        }

        return await window.siteApi.create(clientId, requestBody, { acceptStream: false });
    }

    function initPicker(picker) {
        if (picker.dataset.sitePickerReady === 'true') return;

        var select = picker.querySelector('[data-site-picker-select]');
        var clientSelector = picker.dataset.clientSelect;
        var clientSelect = clientSelector ? document.querySelector(clientSelector) : null;
        var newAddressOption = picker.querySelector('[data-site-picker-new-address]');
        var newAddressInput = picker.querySelector('[data-site-picker-new-address-input]');
        var newAddressSave = picker.querySelector('[data-site-picker-new-address-save]');
        var newAddressCancel = picker.querySelector('[data-site-picker-new-address-cancel]');

        if (!select || !clientSelect) return;
        picker.dataset.sitePickerReady = 'true';
        picker.dataset.sitePickerCreatingAddress = 'false';

        function syncNewAddressState(visible) {
            if (newAddressOption) {
                if (visible) newAddressOption.classList.remove('d-none');
                else newAddressOption.classList.add('d-none');
            }
            if (!visible) {
                clearNewAddressInputs(picker);
            }
        }

        async function load(preferredId) {
            var clientId = clientSelect.value;
            if (!clientId) {
                renderAddresses(select, [], '', false);
                syncNewAddressState(false);
                return;
            }

            var selectedValue = typeof preferredId !== 'undefined' ? preferredId : select.value;
            try {
                setLoading(select, true);
                var loaded = await fetchAddresses(clientId);
                renderAddresses(select, loaded.sites, selectedValue, true, loaded.defaultId);
                picker.dataset.sitePickerHasSites = loaded.sites.length ? 'true' : 'false';
                // A dropdown whose only entry is "+ Add new site address" is
                // no choice at all: go straight to the address input.
                if (!loaded.sites.length) openNewAddressEditor(false);
            } catch (error) {
                console.warn('site_picker: failed to load addresses', error);
                destroyChoices(select);
                select.innerHTML = '<option value="">Unable to load addresses</option>';
                select.disabled = false;
                initChoices(select);
            }
        }

        function openNewAddressEditor(shouldFocus) {
            var clientId = clientSelect.value;
            if (!clientId) {
                setNewAddressError(picker, 'Select a client first.');
                return;
            }
            var currentSelectValue = select.value || '';
            picker.dataset.sitePickerPreviousValue = currentSelectValue === NEW_ADDRESS_VALUE ? '' : currentSelectValue;
            syncNewAddressState(true);
            showNewAddressForm(picker, shouldFocus);
            setPickerValue(select, '');
        }

        async function saveNewAddress() {
            var clientId = clientSelect.value;
            if (!clientId) {
                setNewAddressError(picker, 'Select a client first.');
                return false;
            }

            var previousValue = select.value;
            try {
                if (newAddressSave) newAddressSave.disabled = true;
                if (newAddressCancel) newAddressCancel.disabled = true;
                var created = await createAddress(picker, clientId);
                if (!created) return false;

                hideNewAddressForm(picker);
                syncNewAddressState(false);
                picker.dataset.sitePickerPreviousValue = '';
                await load(String(created.id));
                select.focus();
                return true;
            } catch (error) {
                setPickerValue(select, picker.dataset.sitePickerPreviousValue || previousValue || '');
                setNewAddressError(picker, error.message || 'Unable to create address');
                return false;
            } finally {
                if (newAddressSave) newAddressSave.disabled = false;
                if (newAddressCancel) newAddressCancel.disabled = false;
            }
        }

        function resetPicker() {
            hideNewAddressForm(picker);
            setPickerValue(select, '');
            load();
        }

        clientSelect.addEventListener('change', function () {
            hideNewAddressForm(picker);
            picker.dataset.sitePickerPreviousValue = '';
            setPickerValue(select, '');
            load();
        });
        if (newAddressSave) newAddressSave.addEventListener('click', saveNewAddress);
        if (newAddressCancel) newAddressCancel.addEventListener('click', function () {
            var previousValue = picker.dataset.sitePickerPreviousValue || '';
            hideNewAddressForm(picker);
            picker.dataset.sitePickerPreviousValue = '';
            setPickerValue(select, previousValue);
            load();
        });
        if (newAddressInput) {
            newAddressInput.addEventListener('keydown', function (event) {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    saveNewAddress();
                } else if (event.key === 'Escape') {
                    hideNewAddressForm(picker);
                    load();
                }
            });
        }

        select.addEventListener('change', function () {
            if (select.value === NEW_ADDRESS_VALUE) {
                openNewAddressEditor(true);
                return;
            }

            if (picker.dataset.sitePickerCreatingAddress === 'true') {
                hideNewAddressForm(picker);
            }
        });

        var form = getPickerForm(picker);
        if (form && !form.dataset.sitePickerResetBound) {
            form.dataset.sitePickerResetBound = 'true';
            form.addEventListener('reset', function () {
                resetPicker();
            });
            form.addEventListener('submit', async function (event) {
                if (picker.dataset.sitePickerCreatingAddress !== 'true') return;
                if (picker.dataset.sitePickerAutoSaving === 'true') return;

                if (!readField(picker, 'input').trim()) {
                    // With no saved site the panel is the only way to give
                    // one, so it stays while the server reports the gap.
                    if (picker.dataset.sitePickerHasSites !== 'true') return;
                    hideNewAddressForm(picker);
                    setPickerValue(select, picker.dataset.sitePickerPreviousValue || '');
                    picker.dataset.sitePickerPreviousValue = '';
                    return;
                }

                event.preventDefault();
                picker.dataset.sitePickerAutoSaving = 'true';
                var saved = await saveNewAddress();
                picker.dataset.sitePickerAutoSaving = 'false';

                if (!saved) {
                    var focusInput = fieldEl(picker, 'input');
                    if (focusInput) focusInput.focus();
                    return;
                }
                if (typeof form.requestSubmit === 'function') form.requestSubmit();
                else form.submit();
            });
        }

        load();
    }

    function resetSitePickers() {
        document.querySelectorAll('[data-site-picker]').forEach(function (picker) {
            var select = picker.querySelector('[data-site-picker-select]');
            if (!select) return;
            hideNewAddressForm(picker);
            setPickerValue(select, '');
        });
    }

    // Public API: quote_form.js calls this after a successful submit to clear any
    // pending picker state. Exposed regardless of Stimulus so the contract holds
    // even if the runtime is absent.
    window.resetSitePickers = resetSitePickers;

export default class extends Controller {
        connect() {
            this._onBeforeCache = () => this._teardown();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
            initPicker(this.element);
        }

        disconnect() {
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this._teardown();
        }

        // Destroy Choices and clear the DOM-dataset init guards before Turbo
        // caches the snapshot. The guards (sitePickerReady on the picker,
        // sitePickerResetBound on the form) would otherwise survive in the cached
        // markup and block re-init on restore while the Choices instance is gone.
        _teardown() {
            var picker = this.element;
            var select = picker.querySelector('[data-site-picker-select]');
            destroyChoices(select);
            hideNewAddressForm(picker);
            delete picker.dataset.sitePickerReady;
            var form = picker.closest('form');
            if (form) delete form.dataset.sitePickerResetBound;
        }
}
